// =============================================================================
// staticSite/cfPagesDeployer — wysylka plikow na Cloudflare Pages (Direct Upload).
//
// Krok po kroku (jak wrangler pages deploy):
//   1. hash per plik = blake3(base64(bajty) + rozszerzenie).hex[0:32]
//   2. upload-token (JWT) -> check-missing (ktorych CF nie ma)
//   3. upload brakujacych w paczkach (<= 500 plikow / <= 6 MB base64, 3 rownolegle, retry)
//   4. upsert-hashes (wszystkie) -> POST deployments (multipart: manifest + _redirects + _headers)
//   5. poll az latest_stage.status == success
// Czyste JS (blake3 z @noble/hashes — CSP bez wasm). Pliki _redirects/_headers
// NIE wchodza do manifestu — ida jako osobne pola formularza.
// =============================================================================

import { blake3 } from "@noble/hashes/blake3";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils";
import type { RenderedFile } from "./types";
import {
  checkMissingAssets, createDeployment, getDeployment, getUploadToken, uploadAssets, upsertHashes,
  type CfAssetUpload, type CfDeployment,
} from "./cfPagesApi";

export const CF_PAGES_MAX_FILES = 20_000;
export const CF_PAGES_MAX_FILE_BYTES = 25 * 1024 * 1024;
export const BUCKET_MAX_FILES = 500;
export const BUCKET_MAX_BASE64_BYTES = 6 * 1024 * 1024;
const UPLOAD_CONCURRENCY = 3;
const UPLOAD_RETRIES = 5;
const SPECIAL_FILES = new Set(["/_redirects", "/_headers", "/_routes.json"]);

export function bytesToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined" && typeof (Buffer as any).from === "function") {
    return (Buffer as any).from(bytes).toString("base64");
  }
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as unknown as number[]);
  }
  return btoa(binary);
}

/** Rozszerzenie bez kropki ("html", "jpg", "" gdy brak). */
export function extensionOf(path: string): string {
  const last = path.split("/").pop() || "";
  const i = last.lastIndexOf(".");
  return i > 0 ? last.slice(i + 1) : "";
}

/** Hash zgodny z wrangler: blake3(base64 + ext) hex, 32 znaki. */
export function hashFile(bytes: Uint8Array, path: string, base64?: string): string {
  const b64 = base64 ?? bytesToBase64(bytes);
  return bytesToHex(blake3(utf8ToBytes(b64 + extensionOf(path)))).slice(0, 32);
}

export interface HashedFile {
  path: string;
  hash: string;
  base64: string;
  contentType: string;
  bytes: number;
}

export function hashFiles(files: RenderedFile[]): HashedFile[] {
  return files
    .filter((f) => !SPECIAL_FILES.has(f.path))
    .map((f) => {
      const base64 = bytesToBase64(f.bytes);
      return { path: f.path, hash: hashFile(f.bytes, f.path, base64), base64, contentType: f.contentType, bytes: f.bytes.byteLength };
    });
}

export function buildManifest(hashed: HashedFile[]): Record<string, string> {
  const manifest: Record<string, string> = {};
  for (const f of hashed) manifest[f.path] = f.hash;
  return manifest;
}

/** Dzieli pliki na paczki wg limitow (liczba + rozmiar base64). */
export function bucketize<T extends { base64: string }>(items: T[], maxFiles = BUCKET_MAX_FILES, maxBytes = BUCKET_MAX_BASE64_BYTES): T[][] {
  const buckets: T[][] = [];
  let cur: T[] = [];
  let curBytes = 0;
  for (const it of items) {
    const size = it.base64.length;
    if (cur.length && (cur.length >= maxFiles || curBytes + size > maxBytes)) {
      buckets.push(cur);
      cur = [];
      curBytes = 0;
    }
    cur.push(it);
    curBytes += size;
  }
  if (cur.length) buckets.push(cur);
  return buckets;
}

export function validateFiles(files: RenderedFile[]): string[] {
  const errors: string[] = [];
  if (files.length > CF_PAGES_MAX_FILES) errors.push(`Za dużo plików: ${files.length} > ${CF_PAGES_MAX_FILES} (limit Cloudflare Pages).`);
  for (const f of files) {
    if (f.bytes.byteLength > CF_PAGES_MAX_FILE_BYTES) errors.push(`${f.path}: ${(f.bytes.byteLength / 1024 / 1024).toFixed(1)} MB > 25 MB.`);
  }
  return errors;
}

export interface DeployProgress {
  (ev: { stage: "hash" | "check" | "upload" | "deploy" | "poll"; current?: number; total?: number; message: string }): void;
}

export interface DeploySiteArgs {
  token: string;
  accountId: string;
  project: string;
  branch: string;
  files: RenderedFile[];
  onProgress?: DeployProgress;
}

export interface DeploySiteResult {
  deployment: CfDeployment;
  manifest: Record<string, string>;
  fileCount: number;
  bytesTotal: number;
  uploadedCount: number;
}

async function withRetry<T>(fn: () => Promise<T>, label: string, retries = UPLOAD_RETRIES): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      const msg = e instanceof Error ? e.message : String(e);
      // 401/403 = zly token — bez sensu powtarzac
      if (/HTTP 40[13]|10000:|authentication/i.test(msg)) throw e;
      await new Promise((r) => setTimeout(r, Math.min(15_000, 1000 * 2 ** (attempt - 1))));
    }
  }
  throw new Error(`${label}: ${lastErr instanceof Error ? lastErr.message : String(lastErr)}`);
}

export async function deploySite(args: DeploySiteArgs): Promise<DeploySiteResult> {
  const progress = args.onProgress || (() => {});
  const errors = validateFiles(args.files);
  if (errors.length) throw new Error(errors.join(" "));

  progress({ stage: "hash", message: "Liczę hashe plików" });
  const hashed = hashFiles(args.files);
  const manifest = buildManifest(hashed);
  const bytesTotal = hashed.reduce((s, f) => s + f.bytes, 0);
  const redirects = args.files.find((f) => f.path === "/_redirects");
  const headers = args.files.find((f) => f.path === "/_headers");

  progress({ stage: "check", message: "Sprawdzam, których plików Cloudflare jeszcze nie ma" });
  const jwt = await getUploadToken(args.token, args.accountId, args.project);
  const allHashes = [...new Set(hashed.map((f) => f.hash))];
  const missing = new Set<string>();
  for (const chunk of chunkArray(allHashes, 1000)) {
    for (const h of await withRetry(() => checkMissingAssets(jwt, chunk), "check-missing")) missing.add(h);
  }
  const byHash = new Map<string, HashedFile>();
  for (const f of hashed) if (missing.has(f.hash) && !byHash.has(f.hash)) byHash.set(f.hash, f);
  const toUpload = [...byHash.values()];
  const buckets = bucketize(toUpload);
  progress({ stage: "upload", current: 0, total: toUpload.length, message: `Do wgrania ${toUpload.length} z ${allHashes.length} plików (${buckets.length} paczek)` });

  let uploaded = 0;
  let idx = 0;
  const worker = async () => {
    while (idx < buckets.length) {
      const bucket = buckets[idx++];
      const payload: CfAssetUpload[] = bucket.map((f) => ({ key: f.hash, value: f.base64, metadata: { contentType: f.contentType }, base64: true }));
      await withRetry(() => uploadAssets(jwt, payload), "upload");
      uploaded += bucket.length;
      progress({ stage: "upload", current: uploaded, total: toUpload.length, message: `Wgrano ${uploaded}/${toUpload.length}` });
    }
  };
  await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, Math.max(1, buckets.length)) }, worker));

  for (const chunk of chunkArray(allHashes, 1000)) {
    await withRetry(() => upsertHashes(jwt, chunk), "upsert-hashes");
  }

  progress({ stage: "deploy", message: "Tworzę deployment" });
  const deployment = await withRetry(
    () => createDeployment(args.token, args.accountId, args.project, {
      manifest,
      branch: args.branch,
      redirects: redirects ? (redirects.text ?? new TextDecoder().decode(redirects.bytes)) : undefined,
      headers: headers ? (headers.text ?? new TextDecoder().decode(headers.bytes)) : undefined,
    }),
    "deployment",
    3,
  );

  progress({ stage: "poll", message: "Czekam, aż Cloudflare zbuduje deployment" });
  const final = await pollDeployment(args.token, args.accountId, args.project, deployment.id, (s) =>
    progress({ stage: "poll", message: `Cloudflare: ${s}` }),
  );

  return { deployment: final, manifest, fileCount: hashed.length, bytesTotal, uploadedCount: uploaded };
}

export async function pollDeployment(
  token: string, accountId: string, project: string, deploymentId: string,
  onStatus?: (status: string) => void, maxTries = 60, intervalMs = 5000,
): Promise<CfDeployment> {
  let last: CfDeployment | null = null;
  for (let i = 0; i < maxTries; i++) {
    last = await getDeployment(token, accountId, project, deploymentId);
    const stage = last.latest_stage;
    const status = stage ? `${stage.name}: ${stage.status}` : "?";
    onStatus?.(status);
    if (stage?.status === "success" && stage.name === "deploy") return last;
    if (stage?.status === "failure" || stage?.status === "canceled") throw new Error(`Deployment ${stage.name} zakończył się statusem ${stage.status}`);
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error(`Deployment nie zakończył się w ${Math.round((maxTries * intervalMs) / 1000)} s (ostatni etap: ${last?.latest_stage?.name} ${last?.latest_stage?.status})`);
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
